import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

/** Дрожание пальца и инерция iOS — не намерение прокрутить. */
const SCROLL_DELTA_PX = 8
/** У конца страницы контент уже не под кнопкой: под неё оставлен `pb-20`. */
const NEAR_END_PX = 16

const scrollerOf = (target: EventTarget | null): Element | null =>
  target instanceof Document ? target.scrollingElement : target instanceof Element ? target : null

/**
 * `true`, пока страницу листают вниз. Вверху, у конца страницы и при прокрутке вверх — `false`.
 * Слушаем в фазе захвата: scroll не всплывает, а страницы прокручивают не только документ.
 * Смена маршрута возвращает кнопку: новая страница начинается сверху, а `hidden`
 * иначе остался бы от прокрутки предыдущей. Прокрутка внутри шторки (`role="dialog"`)
 * и текстового поля — не прокрутка страницы, кнопку не прячет.
 */
export const useHideOnScrollDown = (): boolean => {
  const { pathname } = useLocation()
  const [hidden, setHidden] = useState(false)

  useEffect(() => {
    setHidden(false)
  }, [pathname])

  useEffect(() => {
    const lastTop = new WeakMap<Element, number>()
    const onScroll = (event: Event) => {
      const el = scrollerOf(event.target)
      if (!el || el.tagName === 'TEXTAREA' || el.closest('[role="dialog"]')) return
      const top = el.scrollTop
      const prev = lastTop.get(el) ?? 0
      if (Math.abs(top - prev) < SCROLL_DELTA_PX) return
      lastTop.set(el, top)
      const atEnd = top + el.clientHeight >= el.scrollHeight - NEAR_END_PX
      setHidden(top > prev && !atEnd)
    }
    document.addEventListener('scroll', onScroll, { capture: true, passive: true })
    return () => document.removeEventListener('scroll', onScroll, { capture: true })
  }, [])

  return hidden
}
